/* eslint-disable */
import React, { useState, useRef, useEffect } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import axios from "axios";
import { useLang, LANGUAGES } from "./i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact language switcher for placing inside dashboards (top-right).
 * Persists to localStorage via i18n LangProvider — every component using
 * useLang() will re-render instantly on selection.
 */
const LanguageSwitcher = ({ compact = true }) => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={wrapRef} data-testid="lang-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white text-xs transition-all"
        data-testid="lang-switcher-btn"
        aria-label="Change language"
      >
        <Globe className="w-3.5 h-3.5" />
        <span className="text-base leading-none">{current.flag}</span>
        {!compact && <span className="font-medium">{current.label}</span>}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 min-w-[160px] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden z-50"
          data-testid="lang-switcher-menu"
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => {
                setLang(l.code);
                setOpen(false);
                // Persist preferred_lang to backend so notifications render in this language
                try {
                  const authKeys = ["stockerAuth", "networkMarketerAuth", "managerAuth", "vapeyard_inv"];
                  for (const k of authKeys) {
                    const raw = localStorage.getItem(k);
                    if (raw) {
                      const u = JSON.parse(raw);
                      if (u?.id) {
                        axios.post(`${API}/user/${u.id}/preferred-lang`, { lang: l.code }).catch(() => {});
                        break;
                      }
                    }
                  }
                } catch (_) {}
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                lang === l.code
                  ? "bg-violet-600/20 text-white"
                  : "text-zinc-300 hover:bg-zinc-800"
              }`}
              data-testid={`lang-option-${l.code}`}
            >
              <span className="text-base">{l.flag}</span>
              <span className="flex-1 text-left">{l.label}</span>
              {lang === l.code && <Check className="w-3.5 h-3.5 text-violet-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
